import Breadcrumbs from '@root/components/v2/global/breadcrumbs';
import PageContainer from '@root/components/v2/layout/PageContainer';
import PageContent from '@root/components/v2/layout/PageContent';
import PageHeader from '@root/components/v2/layout/PageHeader';
import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import agent from '@root/agent';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import { useEnvironments, useSelectedEnvironments } from '@root/hooks/environments';
import { TitleTab } from '@root/components/v2/Tab';
import ScanHistoryStats from './components/scanHistoryStats';
import AllScansTab from './components/AllScansTab';
import VulnerabilityScansTab from './components/VulnerabilityScansTab';

dayjs.extend(relativeTime);

const BREADCRUMBS = [{ title: 'Kubernetes Live Scan History' }];

const VALID_TABS = ['malware', 'vulnerability'];
const TAB_ITEMS = [
  { label: 'Malware', value: 'malware' },
  { label: 'Vulnerability', value: 'vulnerability' },
];

// TODO: replace with real API data once the vulnerability scan history endpoint is available.
// const VULNERABILITY_STATS_MOCK = {
//   lastScan: '2h ago',
//   workloadsScanned: 12,
//   openFindings: '6,891',
//   scanErrors: 0,
// };

const KubernetesScanHistory = () => {
  const { cloudProvider } = useCloudProvider();
  const allEnvironments = useEnvironments();
  const { selectedEnvironments } = useSelectedEnvironments();
  const [currentTab, setCurrentTab] = useState('malware');

  const environments = useMemo(() => {
    if (selectedEnvironments.length && cloudProvider !== 'all') {
      return selectedEnvironments.filter(environment => environment.cloudProvider === cloudProvider);
    } else if (cloudProvider === 'all') {
      return selectedEnvironments;
    }
    return allEnvironments;
  }, [selectedEnvironments, cloudProvider, allEnvironments]);

  const environmentIds = useMemo(() => environments.map(e => e.id), [environments]);
  const environmentsReady = environments.length > 0;
  const queriesEnabled = environmentsReady;

  const { data: overviewResponse, isLoading: isLoadingOverview } = useEnhancedQuery(
    ['get_k8s_scan_history_overview', `${environmentIds}`],
    () =>
      agent.Kubernetes.get_k8s_scan_history_overview({
        environments: environmentIds,
        clusters: [],
      }),
    {
      enabled: queriesEnabled,
      staleTime: 30000,
      cacheTime: 300000,
      keepPreviousData: true,
    },
  );

  const { data: vulnerabilityOverviewResponse, isLoading: isLoadingVulnerabilityOverview } = useEnhancedQuery(
    ['get_k8s_vuln_scan_history_overview', `${environmentIds}`],
    () =>
      agent.Kubernetes.get_k8s_vuln_scan_history_overview({
        environments: environmentIds,
        clusters: [],
      }),
    {
      enabled: queriesEnabled && currentTab === 'vulnerability',
      staleTime: 30000,
      cacheTime: 300000,
      keepPreviousData: true,
    },
  );

  const statsData = useMemo(() => {
    const overview = overviewResponse?.data;
    if (!overview) return null;
    return {
      totalScans: overview.total_scans ?? 0,
      scanErrors: overview.total_errors ?? 0,
      lastScan: overview.last_scan ? dayjs(overview.last_scan).fromNow() : '-',
      virus_db_fleet: overview.clamav_db_fleet,
    };
  }, [overviewResponse]);

  const vulnStatsData = useMemo(() => {
    const overview = vulnerabilityOverviewResponse?.data;
    if (!overview) return null;
    return {
      totalScans: overview.total_scans ?? 0,
      scanErrors: overview.total_errors ?? 0,
      lastScan: overview.last_scan_ago ?? '-',
    };
  }, [vulnerabilityOverviewResponse]);

  return (
    <PageContainer>
      <PageHeader title={<Breadcrumbs breadcrumbs={BREADCRUMBS} />}></PageHeader>
      <PageContent>
        <TitleTab
          tabs={currentTab}
          setTabs={setCurrentTab}
          items={TAB_ITEMS}
          validTabs={VALID_TABS}
          enableUrlState={true}
          urlParamName="currentTab"
          containerClasses={{ marginBottom: '15px' }}
        />

        {currentTab === 'malware' ? (
          <>
            <ScanHistoryStats data={statsData} isLoading={isLoadingOverview || !environmentsReady} />
            <div className="mt-4 w-full h-full">
              <AllScansTab environments={environments} enabled={queriesEnabled} tabType="malware" />
            </div>
          </>
        ) : (
          <>
            <ScanHistoryStats data={vulnStatsData} isLoading={isLoadingVulnerabilityOverview} />
            <div className="mt-4 w-full h-full">
              <VulnerabilityScansTab environments={environments} enabled={queriesEnabled} tabType="vulnerability" />
            </div>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default KubernetesScanHistory;
